// Bank statements and checks: the amount in words for a check, reading OFX/QFX files, and matching statement lines to the books.
import { addDays } from './dates.js';

const ONES = ['', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten', 'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen', 'seventeen', 'eighteen', 'nineteen'];
const TENS = ['', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety'];

function below1000(n: number): string {
  const parts: string[] = [];
  if (n >= 100) { parts.push(`${ONES[Math.floor(n / 100)]} hundred`); n %= 100; }
  if (n >= 20) { const t = TENS[Math.floor(n / 10)]!; parts.push(n % 10 ? `${t}-${ONES[n % 10]}` : t); }
  else if (n > 0) parts.push(ONES[n]!);
  return parts.join(' ');
}

/** The amount as written on a check: "One thousand two hundred thirty-four and 56/100". */
export function amountInWords(cents: number): string {
  if (!Number.isInteger(cents) || cents < 0 || cents > 99_999_999_999) throw new Error('A check amount must be zero or more, in whole cents');
  const dollars = Math.floor(cents / 100); const c = cents % 100;
  const scales: [number, string][] = [[1_000_000_000, 'billion'], [1_000_000, 'million'], [1000, 'thousand']];
  let rest = dollars; const words: string[] = [];
  for (const [size, name] of scales) { if (rest >= size) { words.push(`${below1000(Math.floor(rest / size))} ${name}`); rest %= size; } }
  if (rest > 0 || words.length === 0) words.push(rest === 0 ? 'zero' : below1000(rest));
  const text = words.join(' ');
  return `${text.charAt(0).toUpperCase()}${text.slice(1)} and ${String(c).padStart(2, '0')}/100`;
}

export interface StatementLine { postedOn: string; amountCents: number; description: string; fitid: string | null; checkNo: string | null }

const tag = (block: string, name: string) => { const m = new RegExp(`<${name}>([^<\\r\\n]*)`, 'i').exec(block); return m ? m[1]!.trim() : null; };
const ofxDate = (s: string | null) => (s && /^\d{8}/.test(s) ? `${s.slice(0, 4)}-${s.slice(4, 6)}-${s.slice(6, 8)}` : null);

/** Transactions from an OFX or QFX file (what banks and QuickBooks call a "Web Connect" download). Deposits are positive, payments negative. */
export function parseOfx(text: string): StatementLine[] {
  const out: StatementLine[] = [];
  for (const m of text.matchAll(/<STMTTRN>([\s\S]*?)(?:<\/STMTTRN>|(?=<STMTTRN>)|<\/BANKTRANLIST>)/gi)) {
    const b = m[1]!;
    const date = ofxDate(tag(b, 'DTPOSTED')); const amt = tag(b, 'TRNAMT');
    if (!date || amt === null) continue;
    const n = Number(amt.replace(/,/g, ''));
    if (!Number.isFinite(n)) continue;
    const name = [tag(b, 'NAME'), tag(b, 'MEMO')].filter(Boolean).join(' ').replace(/&amp;/g, '&');
    out.push({ postedOn: date, amountCents: Math.round(n * 100), description: name, fitid: tag(b, 'FITID'), checkNo: tag(b, 'CHECKNUM') });
  }
  return out;
}

/** A bank's date text as YYYY-MM-DD. Understands 2026-10-23, 10/23/2026, 10/23/26 and 23-Oct-2026. */
export function parseBankDate(s: string): string | null {
  const t = s.trim();
  let m = /^(\d{4})-(\d{2})-(\d{2})/.exec(t); if (m) return `${m[1]}-${m[2]}-${m[3]}`;
  m = /^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/.exec(t);
  if (m) { const y = m[3]!.length === 2 ? `20${m[3]}` : m[3]!; return `${y}-${m[1]!.padStart(2, '0')}-${m[2]!.padStart(2, '0')}`; }
  m = /^(\d{1,2})[- ]([A-Za-z]{3})[a-z]*[- ](\d{2,4})$/.exec(t);
  if (m) {
    const mon = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'].indexOf(m[2]!.toLowerCase());
    if (mon >= 0) return `${m[3]!.length === 2 ? `20${m[3]}` : m[3]}-${String(mon + 1).padStart(2, '0')}-${m[1]!.padStart(2, '0')}`;
  }
  return null;
}

/** A bank's amount text as cents: "$1,234.56", "(45.00)", "-45.00" and "45.00-" are all handled. */
export function parseBankAmount(s: string): number | null {
  let t = s.trim().replace(/[$,\s]/g, '');
  if (t === '') return null;
  let neg = false;
  if (/^\(.*\)$/.test(t)) { neg = true; t = t.slice(1, -1); }
  if (t.endsWith('-')) { neg = true; t = t.slice(0, -1); }
  if (t.startsWith('-')) { neg = !neg; t = t.slice(1); }
  if (!/^\d+(\.\d{1,2})?$/.test(t)) return null;
  const c = Math.round(Number(t) * 100);
  return neg ? -c : c;
}

export interface LedgerLine { id: number | string; date: string; amountCents: number; memo: string }

/**
 * Pairs statement lines with unreconciled books entries: same amount, dates within `days` of each other,
 * and a check number found in the memo wins over date. Each books line is used once.
 */
export function matchStatement(statement: (StatementLine & { id?: number | string })[], ledger: LedgerLine[], days = 10): { statementIndex: number; ledgerId: number | string }[] {
  const used = new Set<number | string>(); const out: { statementIndex: number; ledgerId: number | string }[] = [];
  const dist = (a: string, b: string) => Math.abs(Date.parse(`${a}T12:00:00Z`) - Date.parse(`${b}T12:00:00Z`)) / 86_400_000;
  statement.forEach((s, i) => {
    const cands = ledger.filter((l) => !used.has(l.id) && l.amountCents === s.amountCents && dist(l.date, s.postedOn) <= days);
    if (cands.length === 0) return;
    const byCheck = s.checkNo ? cands.find((l) => new RegExp(`(^|\\D)${s.checkNo!.replace(/^0+/, '')}(\\D|$)`).test(l.memo)) : undefined;
    const best = byCheck ?? [...cands].sort((a, b) => dist(a.date, s.postedOn) - dist(b.date, s.postedOn))[0]!;
    used.add(best.id); out.push({ statementIndex: i, ledgerId: best.id });
  });
  return out;
}

/** The date a check dated today should show, kept for callers that print a few days ahead. */
export const checkDate = (iso: string, plusDays = 0) => addDays(iso, plusDays);
